import axios from 'axios';

const rawBase = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || 'http://localhost:5001';
const API_BASE_URL = rawBase.replace(/\/api\/?$/, '');

const SA_TOKEN_KEY = 'anygrc_sa_token';
const SA_USER_KEY = 'anygrc_sa_user';

export interface SuperAdminUser {
  login_id: string;
  name?: string;
  roles: string[];
}

export const saClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
});

saClient.interceptors.request.use((config) => {
  const token = sessionStorage.getItem(SA_TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const superAdminAuth = {
  async login(login_id: string, password: string): Promise<SuperAdminUser> {
    const { data } = await saClient.post('/api/v1/auth/super-admin/login', { login_id, password });
    sessionStorage.setItem(SA_TOKEN_KEY, data.access_token);
    const payload = JSON.parse(atob(data.access_token.split('.')[1]));
    const user: SuperAdminUser = { login_id: payload.sub, name: payload.name, roles: payload.roles || [] };
    sessionStorage.setItem(SA_USER_KEY, JSON.stringify(user));
    return user;
  },
  logout() {
    sessionStorage.removeItem(SA_TOKEN_KEY);
    sessionStorage.removeItem(SA_USER_KEY);
  },
  current(): SuperAdminUser | null {
    const raw = sessionStorage.getItem(SA_USER_KEY);
    return raw ? JSON.parse(raw) : null;
  },
  isAuthenticated(): boolean {
    return !!sessionStorage.getItem(SA_TOKEN_KEY);
  },
};

export interface EntityRow {
  id: number;
  name: string;
  code: string;
  group_id?: number | null;
  cin?: string | null;
  pan?: string | null;
  incorporation_date?: string | null;
  constitution?: string | null;
  contact_person?: string | null;
  contact_phone?: string | null;
  contact_email?: string | null;
  address?: string | null;
  is_active: number;
  is_approved: number;
}

export const entitiesApi = {
  list: async (): Promise<EntityRow[]> => (await saClient.get('/api/v1/super-admin/entities')).data,
  create: async (payload: Partial<EntityRow>): Promise<EntityRow> =>
    (await saClient.post('/api/v1/super-admin/entities', payload)).data,
  update: async (id: number, payload: Partial<EntityRow>): Promise<EntityRow> =>
    (await saClient.put(`/api/v1/super-admin/entities/${id}`, payload)).data,
  toggleStatus: async (id: number, is_active: number): Promise<EntityRow> =>
    (await saClient.patch(`/api/v1/super-admin/entities/${id}/status`, { is_active })).data,
  remove: async (id: number): Promise<void> => {
    await saClient.delete(`/api/v1/super-admin/entities/${id}`);
  },
};

export interface SAUserRow {
  id: number;
  login_id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string | null;
  is_active: number;
  is_approved: number;
  entity_ids: number[];
  /** Entities the user is a ROOT entity administrator of (is_entity_admin=1). */
  admin_entity_ids: number[];
  role_ids: number[];
  role_names: string[];
}

export interface SAUserPayload {
  login_id?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  password?: string;
  entity_ids?: number[];
  role_ids?: number[];
  is_active?: number;
  is_approved?: number;
}

export const usersApi = {
  list: async (): Promise<SAUserRow[]> => (await saClient.get('/api/v1/super-admin/users')).data,
  create: async (p: SAUserPayload): Promise<SAUserRow> =>
    (await saClient.post('/api/v1/super-admin/users', p)).data,
  update: async (id: number, p: SAUserPayload): Promise<SAUserRow> =>
    (await saClient.put(`/api/v1/super-admin/users/${id}`, p)).data,
  toggleStatus: async (id: number, is_active: number): Promise<SAUserRow> =>
    (await saClient.patch(`/api/v1/super-admin/users/${id}/status`, { is_active })).data,
  // Surgical entity-admin appoint/revoke — touch ONLY the (user, entity) pair.
  // Grant flips is_entity_admin=1 + grants Entity Administrator for that entity.
  // Revoke clears is_entity_admin (membership/login PRESERVED) + removes that
  // entity's Entity Administrator role. Neither rebuilds the user's entity map.
  grantEntityAdmin: async (userId: number, entityId: number): Promise<SAUserRow> =>
    (await saClient.post(`/api/v1/super-admin/users/${userId}/entity-admin/${entityId}`)).data,
  revokeEntityAdmin: async (userId: number, entityId: number): Promise<SAUserRow> =>
    (await saClient.delete(`/api/v1/super-admin/users/${userId}/entity-admin/${entityId}`)).data,
  checkUnique: async (params: { login_id?: string; email?: string; exclude_id?: number }):
    Promise<{ login_id_taken: boolean; email_taken: boolean }> =>
    (await saClient.get('/api/v1/super-admin/users/check-unique', { params })).data,
};

export interface RoleRow { id: number; role_name: string; role_description?: string | null; }
export interface GroupRow { id: number; name: string; }

export const lookupsApi = {
  roles: async (): Promise<RoleRow[]> => (await saClient.get('/api/v1/super-admin/roles')).data,
  groups: async (): Promise<GroupRow[]> => (await saClient.get('/api/v1/super-admin/groups')).data,
};
